import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Fade, IconButton, Modal, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useVideo } from "../../../../context/juno/VideoContext";
import InputPrompt from "./InputPrompt";
import ImageUpload from "./ImageUpload";
import { ColumnBox, ColumnCenteredBox, RowBox } from "../../../../utils/styledBox";
import { alpha } from "@mui/material/styles";
import ModelVersionSelectBox from "./ModelVersionSelectBox";
import { GradientTypography } from "../../../../utils/gradientTypography";
import LumaConceptsControl from "./params/LumaConceptsControl";

interface ExtendModalProps {
  open: boolean;
  onClose: () => void;
  videoUuid: string;
  videoUrl?: string;
}

/**
 * 動画拡張用モーダル
 */
const ExtendModal: React.FC<ExtendModalProps> = ({ open, onClose, videoUuid, videoUrl = "" }) => {
  const { t } = useTranslation();
  const { setExtendId, setExtendType, image1, setImage1, aiEngine, updateParams, modelVersion } = useVideo();
  const [direction, setDirection] = useState<"start" | "end">("start");

  // 方向が変更されたときにExtendTypeを更新
  useEffect(() => {
    setExtendType(direction);
  }, [direction, setExtendType]);

  // モーダルが開かれたときにExtendIdを設定
  useEffect(() => {
    if (open) {
      setExtendId(videoUuid);
      setImage1("");
    } else {
      // モーダルが閉じられたときにExtendIdを初期化
      setExtendId("");
    }
  }, [open, videoUuid, setExtendId, setImage1]);

  const handleClose = () => {
    setExtendId("");
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "90%", md: "80%", lg: "90%" },
          maxWidth: 1100,
          maxHeight: "90vh",
          overflow: "auto",
          backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
          backdropFilter: "blur(20px)",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} position={"relative"}>
          <ColumnBox sx={{ display: "flex", gap: 2 }}>
            <GradientTypography variant="h6">{t("juno.video.extend.title")}</GradientTypography>
            <Typography variant="body2" color="text.secondary">
              {t("juno.video.extend.description")}
            </Typography>
          </ColumnBox>
          {/* モーダルを閉じるボタン */}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: "absolute", right: -20, top: -20 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* 方向選択ボタン */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, width: "100%" }}>
            <RowBox sx={{ gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", width: 300 }}>
                <ModelVersionSelectBox
                  platform={aiEngine}
                  value={modelVersion[aiEngine]}
                  onChange={(value) => updateParams(aiEngine, "modelVersion", value)}
                  menu={"i2v"}
                />
              </Box>
              <LumaConceptsControl />
            </RowBox>
            <Box sx={{ display: "flex", gap: 2, width: "100%", justifyContent: "flex-end" }}>
              <Button
                variant={direction === "end" ? "contained" : "outlined"}
                onClick={() => setDirection("end")}
                startIcon={<ArrowBackIcon />}
                sx={{ minWidth: 120 }}
                disableElevation
              >
                {t("juno.video.extend.before")}
              </Button>
              <Button
                variant={direction === "start" ? "contained" : "outlined"}
                onClick={() => setDirection("start")}
                startIcon={<ArrowForwardIcon />}
                sx={{ minWidth: 120 }}
                disableElevation
              >
                {t("juno.video.extend.continue")}
              </Button>
            </Box>
          </Box>

          {/* 動画と参照画像の表示エリア */}
          <Box>
            <ColumnCenteredBox
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                mb: 2,
                justifyContent: "center",
                position: "relative",
                minHeight: { xs: 600, md: 440 },
                width: "100%",
              }}
            >
              {/* 動画コンポーネント */}
              {/* 続きモード（動画→画像アップロード） */}
              <Fade
                in={direction === "start"}
                timeout={{ enter: 500, exit: 300 }}
                mountOnEnter
                unmountOnExit
                style={{ position: "absolute", width: "100%", left: "50%", transform: "translateX(-50%)" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 4,
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  {/* 左側: 元の動画表示（続きモード） */}
                  <ColumnBox sx={{ width: { xs: "100%", md: "100%" }, height: "100%" }}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                      {t("juno.video.extend.sourceVideo")}
                    </Typography>
                    <Box sx={{ display: "flex", borderRadius: 2, height: "100%" }}>
                      {videoUrl ? (
                        <video
                          controls
                          autoPlay
                          loop
                          muted
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            maxHeight: 440,
                            borderRadius: 8,
                          }}
                          src={videoUrl}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(0, 0, 0, 0.05)",
                            borderRadius: 2,
                          }}
                        >
                          <CircularProgress size={40} />
                        </Box>
                      )}
                    </Box>
                  </ColumnBox>

                  {/* 右側: 画像アップロード（続きモード） */}
                  <Box sx={{ width: { xs: "100%", md: 300 } }}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                      {t("juno.video.extend.endFrame")}
                    </Typography>
                    <ImageUpload image={image1} setImage={setImage1} />
                  </Box>
                </Box>
              </Fade>

              {/* 前モード（画像アップロード→動画） */}
              <Fade
                in={direction === "end"}
                timeout={{ enter: 500, exit: 300 }}
                mountOnEnter
                unmountOnExit
                style={{ position: "absolute", width: "100%", left: "50%", transform: "translateX(-50%)" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 4,
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  {/* 左側: 画像アップロード（前モード） */}
                  <Box sx={{ width: { xs: "100%", md: 300 } }}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                      {t("juno.video.extend.startFrame")}
                    </Typography>
                    <ImageUpload image={image1} setImage={setImage1} />
                  </Box>

                  {/* 右側: 元の動画表示（前モード） */}
                  <ColumnBox sx={{ width: { xs: "100%", md: "100%" }, height: "100%" }}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                      {t("juno.video.extend.sourceVideo")}
                    </Typography>
                    <Box sx={{ display: "flex", borderRadius: 2, height: "100%" }}>
                      {videoUrl ? (
                        <video
                          controls
                          autoPlay
                          loop
                          muted
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            borderRadius: 8,
                            maxHeight: 440,
                          }}
                          src={videoUrl}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(0, 0, 0, 0.05)",
                            borderRadius: 2,
                          }}
                        >
                          <CircularProgress size={40} />
                        </Box>
                      )}
                    </Box>
                  </ColumnBox>
                </Box>
              </Fade>
            </ColumnCenteredBox>
          </Box>

          {/* InputPromptを表示 */}
          <InputPrompt onSuccess={handleClose} />
        </Box>
      </Paper>
    </Modal>
  );
};

export default ExtendModal;
